<script>
	export let navigate;

	function goBack() {
		if (window.history.length > 1) {
			window.history.back();
		} else if (navigate) {
			navigate('/explorer');
		}
	}
</script>

<div class="guide-page">
	<div class="guide-container">
		<header class="guide-header">
			<button class="back-arrow" on:click={goBack} title="Back">
				<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
					<path d="M10 3L5 8l5 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
				</svg>
			</button>
			<div class="header-text">
				<h1>Email Actions</h1>
				<p class="subtitle">Send to <strong>action@jsond.ca</strong></p>
			</div>
		</header>

		<div class="instructions">
			<h4>Basics</h4>
			<div class="instruction-list">
				<div class="instruction-item">
					<code>any subject</code>
					<span>Create a new document from email body</span>
					<span class="status-tag implemented">implemented</span>
				</div>
				<div class="instruction-item">
					<code>reply to notification</code>
					<span>Update an existing document via reply</span>
					<span class="status-tag implemented">implemented</span>
				</div>
				<div class="instruction-item">
					<code>project-name</code>
					<span>Set the project by using it as the subject line</span>
					<span class="status-tag implemented">implemented</span>
				</div>
				<div class="instruction-item">
					<code>attach files</code>
					<span>File attachments are stored alongside the document</span>
					<span class="status-tag implemented">implemented</span>
				</div>
			</div>

			<h4>Commands</h4>
			<div class="instruction-list">
				<div class="instruction-item">
					<code>[question]</code>
					<span>Research and answer a question using your docs</span>
					<span class="status-tag implemented">implemented</span>
				</div>
				<div class="instruction-item">
					<code>[idea]</code>
					<span>Log an idea to your project's ideation backlog</span>
					<span class="status-tag implemented">implemented</span>
				</div>
				<div class="instruction-item">
					<code>[context][doc-ref]</code>
					<span>Update an existing document with new information</span>
					<span class="status-tag implemented">implemented</span>
				</div>
				<div class="instruction-item">
					<code>[guide]</code>
					<span>Create a structured how-to guide from your content</span>
					<span class="status-tag proposed">proposed</span>
				</div>
				<div class="instruction-item">
					<code>[store]</code>
					<span>Store content verbatim without processing</span>
					<span class="status-tag proposed">proposed</span>
				</div>
				<div class="instruction-item">
					<code>[team]</code>
					<span>Route the document to a specific team</span>
					<span class="status-tag proposed">proposed</span>
				</div>
			</div>

			<h4>Tips</h4>
			<div class="tips-list">
				<p>Commands go in the subject line, content goes in the email body.</p>
				<p>Combine a command with a project name: <code>[question] my-project</code></p>
				<p>Attachments (images, PDFs) are automatically linked to the created document.</p>
				<p>Replying to a notification email threads your update onto the original document.</p>
			</div>

			<h4>Explorer</h4>
			<div class="tips-list">
				<p>Hover any document row and tap <svg width="13" height="13" viewBox="0 0 14 14" style="display:inline-block;vertical-align:middle;margin:0 2px"><circle cx="7" cy="7" r="5.5" fill="none" stroke="currentColor" stroke-width="1"/></svg> to open a quick preview without leaving the explorer.</p>
				<p>The third column shows a topic summary or document preview — select a topic first, then a document.</p>
				<p>Click any document to load its outline in the preview panel. Use <strong>Open full ↗</strong> to open the full document in a focused reading view.</p>
			</div>
		</div>
	</div>
</div>

<style>
	.guide-page {
		min-height: 100vh;
		background: #F9FAFB;
		display: flex;
		justify-content: center;
		padding: 48px 24px;
	}

	.guide-container {
		max-width: 640px;
		width: 100%;
	}

	.guide-header {
		display: flex;
		align-items: flex-start;
		gap: 16px;
		margin-bottom: 36px;
	}

	.back-arrow {
		width: 40px;
		height: 40px;
		border-radius: 50%;
		background: #111827;
		color: #F9FAFB;
		border: none;
		display: flex;
		align-items: center;
		justify-content: center;
		cursor: pointer;
		transition: all 150ms ease-out;
		flex-shrink: 0;
		margin-top: 4px;
	}

	.back-arrow:hover {
		background: #374151;
		transform: scale(1.05);
	}

	.back-arrow:active {
		transform: scale(0.95);
	}

	.header-text h1 {
		font-size: 24px;
		font-weight: 700;
		color: #111827;
		margin: 0;
		letter-spacing: -0.3px;
	}

	.subtitle {
		font-size: 13px;
		color: #6B7280;
		margin: 4px 0 0;
	}

	.subtitle strong {
		color: #374151;
		font-weight: 600;
	}

	/* Instructions - matches Explorer.svelte modal styling */
	.instructions h4 {
		font-size: 11px;
		font-weight: 700;
		text-transform: uppercase;
		letter-spacing: 0.06em;
		color: #111827;
		margin: 0 0 8px;
	}
	.instructions h4:not(:first-child) { margin-top: 28px; }

	.instruction-list {
		display: flex;
		flex-direction: column;
		gap: 6px;
	}

	.instruction-item {
		display: flex;
		align-items: baseline;
		gap: 8px;
		font-size: 12px;
		color: #6B7280;
		line-height: 1.5;
	}

	.instruction-item code {
		font-family: "SF Mono", Monaco, "Cascadia Code", "Roboto Mono", monospace;
		font-size: 11px;
		background: #F3F4F6;
		color: #374151;
		padding: 2px 6px;
		border-radius: 3px;
		flex-shrink: 0;
	}

	.instruction-item span:not(.status-tag) {
		flex: 1;
	}

	.status-tag {
		font-size: 10px;
		font-weight: 600;
		padding: 1px 6px;
		border-radius: 3px;
		flex-shrink: 0;
		letter-spacing: 0.02em;
	}

	.status-tag.implemented {
		background: #DCFCE7;
		color: #166534;
	}

	.status-tag.proposed {
		background: #FEF3C7;
		color: #92400E;
	}

	.tips-list {
		display: flex;
		flex-direction: column;
		gap: 6px;
	}

	.tips-list p {
		font-size: 12px;
		color: #6B7280;
		line-height: 1.5;
		margin: 0;
	}

	.tips-list code {
		font-family: "SF Mono", Monaco, "Cascadia Code", "Roboto Mono", monospace;
		font-size: 11px;
		background: #F3F4F6;
		color: #374151;
		padding: 2px 6px;
		border-radius: 3px;
	}
</style>
